import { GuidanceTourStep } from '../types';

/** Main categories that show steps directly (no sub-category accordion). */
export const FLAT_CHECKLIST_GROUPS = new Set([
  'Getting started',
]);

export function isFlatChecklistGroup(groupName: string): boolean {
  return FLAT_CHECKLIST_GROUPS.has(groupName);
}

const STEP_SUB_GROUP_BY_ID: Record<string, string> = {
  'configure-cdp-settings-card': 'CDP settings',
  'cdp-settings-setup': 'CDP settings',
  'configure-daily-cdp-sync-card': 'Daily CDP Sync',
  'daily-cdp-sync-setup': 'Daily CDP Sync',
  'configure-calendar-card': 'Calendar',
  'calendar-setup': 'Calendar',
  'configure-inventory-feed-card': 'Inventory feed',
  'inventory-feed-setup': 'Inventory feed',
  'myautoiq-settings-setup': 'Myautoiq settings',
  'configure-segments-card': 'Segments',
  'segments-setup': 'Segments',
  'sandbox-readiness-setup': 'Sandbox readiness',
  'sales-portal-settings-setup': 'Sales portal',
  'dealer-dashboard-settings-setup': 'Dealer dashboard',
  'content-dealer-settings': 'Dealer settings',
  'configure-content-card': 'Content',
  'content-value': 'Content',
  'content-footer-image': 'Content',
  'content-offers': 'Content',
  'content-dealer-specials': 'Content',
  'configure-template-value-card': 'Template value',
  'template-value-setup': 'Template value',
  'configure-segment-message-card': 'Message type focus',
  'segment-message-setup': 'Message type focus',
  'template-generations-setup': 'Template generations',
  'configure-static-value-card': 'Static content',
  'static-value-setup': 'Static content',
  'configure-nurturing-journey-card': 'Nurturing journey',
  'nurturing-journey-setup': 'Nurturing journey',
  'configure-nurturing-plan-card': 'Nurturing plan',
  'nurturing-plan-setup': 'Nurturing plan',
  'switchover-note-push-setup': 'Switchover readiness',
  'switchover-nurturing-automation-setup': 'Switchover readiness',
  complete: 'Complete',
};

export type ChecklistStepEntry = {
  step: GuidanceTourStep;
  index: number;
};

export type ChecklistSubGroup = {
  subGroupName: string;
  items: ChecklistStepEntry[];
};

export type ChecklistGroup = {
  groupName: string;
  subGroups: ChecklistSubGroup[];
};

export function getStepSubGroup(step: GuidanceTourStep): string {
  return step.subGroup ?? STEP_SUB_GROUP_BY_ID[step.id] ?? 'Steps';
}

export function buildChecklistHierarchy(steps: GuidanceTourStep[]): ChecklistGroup[] {
  const groups: ChecklistGroup[] = [];
  const groupIndex = new Map<string, ChecklistGroup>();
  const subGroupIndex = new Map<string, ChecklistSubGroup>();

  steps.forEach((step, index) => {
    const groupName = step.group ?? 'General';
    const subGroupName = isFlatChecklistGroup(groupName)
      ? groupName
      : getStepSubGroup(step);

    let group = groupIndex.get(groupName);
    if (!group) {
      group = { groupName, subGroups: [] };
      groupIndex.set(groupName, group);
      groups.push(group);
    }

    const subGroupKey = `${groupName}::${subGroupName}`;
    let subGroup = subGroupIndex.get(subGroupKey);
    if (!subGroup) {
      subGroup = { subGroupName, items: [] };
      subGroupIndex.set(subGroupKey, subGroup);
      group.subGroups.push(subGroup);
    }

    subGroup.items.push({ step, index });
  });

  return groups;
}

export function filterChecklistHierarchy(
  hierarchy: ChecklistGroup[],
  query: string,
): ChecklistGroup[] {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return hierarchy;
  }

  return hierarchy
    .map((group) => {
      const groupMatches = group.groupName.toLowerCase().includes(normalizedQuery);
      const subGroups = group.subGroups
        .map((subGroup) => {
          const subGroupMatches = subGroup.subGroupName
            .toLowerCase()
            .includes(normalizedQuery);
          const items = subGroup.items.filter(
            ({ step }) =>
              groupMatches ||
              subGroupMatches ||
              step.title.toLowerCase().includes(normalizedQuery),
          );
          return { ...subGroup, items };
        })
        .filter((subGroup) => subGroup.items.length > 0);

      return { ...group, subGroups };
    })
    .filter((group) => group.subGroups.length > 0);
}
